import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { SectionHeader } from "../components/Top3";
import TodoistTaskList from "../components/TodoistTaskList";
import EditableText from "../components/EditableText";

const Block = ({ code, label, children, testid, labelKey }) => (
  <section data-testid={testid}>
    <SectionHeader code={code} label={label} labelKey={labelKey} />
    <div className="op-panel p-6">{children}</div>
  </section>
);

const EditableList = ({ items, testid, onChange, placeholder = "Add item…" }) => {
  const list = Array.isArray(items) ? items : [];
  const update = (idx, val) => {
    const next = [...list];
    if (val == null || val === "") next.splice(idx, 1);
    else next[idx] = val;
    onChange(next);
  };
  const add = (val) => {
    if (!val || !val.trim()) return;
    onChange([...list, val.trim()]);
  };
  return (
    <ul className="space-y-3" data-testid={testid}>
      {list.map((it, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="op-mono text-[10px] text-[#5E657D] mt-1">{String(i + 1).padStart(2, "0")}</span>
          <EditableText
            value={it}
            onSave={(v) => update(i, v)}
            className="text-white text-sm leading-snug flex-1"
            multiline
            testid={`${testid}-item-${i}`}
          />
        </li>
      ))}
      <li className="flex items-start gap-3">
        <span className="op-mono text-[10px] text-[#00E5FF] mt-1">+</span>
        <EditableText
          value=""
          onSave={(v) => add(v)}
          className="text-[#5E657D] text-sm italic leading-snug flex-1"
          emptyPlaceholder={placeholder}
          testid={`${testid}-add`}
        />
      </li>
    </ul>
  );
};

const BusinessPage = ({ slug: propSlug }) => {
  const params = useParams();
  const slug = propSlug || params.slug;
  const [data, setData] = useState(null);
  const [projectId, setProjectId] = useState(null);

  const load = () => api.business(slug).then(setData).catch(() => setData({ label: "NOT FOUND", current_priority: "" }));

  useEffect(() => {
    setData(null);
    setProjectId(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Resolve project id once we know the business label
  useEffect(() => {
    if (!data?.label) return;
    api.todoistRouting()
      .then((r) => setProjectId((r.business_map || {})[data.label] || null))
      .catch(() => setProjectId(null));
  }, [data?.label]);

  if (!data) return <div className="px-6 md:px-12 py-14 text-[#5E657D] op-microcopy">LOADING · {slug}</div>;

  const patchListField = async (field, next) => {
    setData((d) => ({ ...d, [field]: next }));
    try { await api.patchBusiness(slug, { [field]: next }); }
    catch { load(); }
  };
  const patchStrField = async (field, next) => {
    setData((d) => ({ ...d, [field]: next }));
    try { await api.patchBusiness(slug, { [field]: next }); }
    catch { load(); }
  };

  return (
    <div className="px-6 md:px-12 py-10 md:py-14 max-w-[1600px] mx-auto space-y-12" data-testid={`business-${slug}`}>
      <header className="relative">
        <div className="op-microcopy text-[#00E5FF] mb-4">BUSINESS · {slug.toUpperCase()}</div>
        <EditableText
          as="h1"
          value={data.label}
          onSave={(v) => patchStrField("label", v)}
          className="text-white text-4xl md:text-6xl font-light tracking-tight mb-3 block"
          testid={`business-${slug}-label`}
        />
        <EditableText
          value={data.current_priority || ""}
          onSave={(v) => patchStrField("current_priority", v)}
          className="text-[#A0A5B5] text-lg max-w-3xl block"
          multiline
          emptyPlaceholder="Add the current priority for this business…"
          testid={`business-${slug}-priority`}
        />
        <div className="op-hairline mt-8" />
      </header>

      <TodoistTaskList
        mode="project"
        projectId={projectId}
        code="TD"
        label={`TODOIST · ${data.label}`}
        caption="Active tasks in this business's mapped Todoist project."
        testid={`biz-todoist-${slug}`}
        emptyText="No active Todoist tasks in this project. Nothing waiting."
        notConnectedText="Todoist is not connected yet. Add your token in Settings to pull tasks for this business."
        noProjectText={`Map "${data.label}" to a Todoist project in Settings → Project Routing to see tasks here.`}
        showProjectChips
        showSearch
        showPromote
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Block code="01" label="TOP TASKS" testid="biz-top-tasks" labelKey={`biz.${slug}.top-tasks.label`}>
            <EditableList
              items={data.top_tasks}
              onChange={(next) => patchListField("top_tasks", next)}
              testid="biz-top-tasks-list"
              placeholder="Add top task…"
            />
          </Block>
          <Block code="02" label="ACTIVE PROJECTS" testid="biz-projects" labelKey={`biz.${slug}.projects.label`}>
            <EditableList
              items={data.active_projects}
              onChange={(next) => patchListField("active_projects", next)}
              testid="biz-projects-list"
              placeholder="Add project…"
            />
          </Block>
          <Block code="03" label="RECENT MEETING ACTION ITEMS" testid="biz-meetings" labelKey={`biz.${slug}.meetings.label`}>
            <EditableList
              items={data.meeting_action_items}
              onChange={(next) => patchListField("meeting_action_items", next)}
              testid="biz-meetings-list"
              placeholder="Add action item…"
            />
          </Block>
        </div>
        <div className="space-y-6">
          <Block code="04" label="WAITING ON" testid="biz-waiting" labelKey={`biz.${slug}.waiting.label`}>
            <EditableList
              items={data.waiting_on}
              onChange={(next) => patchListField("waiting_on", next)}
              testid="biz-waiting-list"
              placeholder="Add waiting item…"
            />
          </Block>
          <Block code="05" label="UPCOMING CALENDAR" testid="biz-calendar" labelKey={`biz.${slug}.calendar.label`}>
            <EditableList
              items={data.upcoming_calendar}
              onChange={(next) => patchListField("upcoming_calendar", next)}
              testid="biz-calendar-list"
              placeholder="Add event…"
            />
          </Block>
          <Block code="06" label="QUICK NOTES" testid="biz-notes" labelKey={`biz.${slug}.notes.label`}>
            <EditableText
              value={data.quick_notes || ""}
              onSave={(v) => patchStrField("quick_notes", v)}
              className="text-white text-sm leading-relaxed whitespace-pre-wrap block"
              multiline
              emptyPlaceholder="Add notes for this business…"
              testid="biz-notes-text"
            />
          </Block>
        </div>
      </div>
    </div>
  );
};

export default BusinessPage;
